import React, { useEffect, useState } from 'react';
import { upscaleConfig, subscribeUpscaleConfig } from '../utils/upscaleDefaults';
import { saveUpscaleConfig } from '../data/upscaleConfigSync';
import { bakeEstimate, outMpFor, GPU_HOURLY_USD } from '../utils/upscaleEstimate';
import { fmtDuration } from '../utils/eta';
import { fmtUSD } from '../utils/cost';

// Words in the SUPIR positive prompt that coax invented water/texture — flagged
// because they're what made the earlier bake read as an "AI painting".
const INVENT_WORDS = ['spray', 'texture', 'droplet', 'foam', 'detailed water', 'highly detailed'];

const Row = ({ ok, warn, label, value, note }) => (
  <div className={`cs-pf-row${warn ? ' is-warn' : ''}${ok === false && !warn ? ' is-bad' : ''}`}>
    <span className="cs-pf-ic">{warn ? '⚠' : ok === false ? '✕' : '✓'}</span>
    <span className="cs-pf-label">{label}</span>
    <span className="cs-pf-value">{value}</span>
    {note && <span className="cs-pf-note">{note}</span>}
  </div>
);

// plan: { label, frames, scale, model, lookName, cropW, cropH, subjectCentered }
export default function BakePreflight({ plan, onClose, onProceed }) {
  const [cfg, setCfg] = useState(() => upscaleConfig());
  const [hourly, setHourly] = useState(GPU_HOURLY_USD);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  useEffect(() => subscribeUpscaleConfig(setCfg), []);

  const s = cfg.models?.supir || {};
  const poll = cfg.poll || {};
  const model = plan.model || 'supir';
  const mode = model === 'supir' ? (s.mode || 'supir-only') : model;
  const frames = plan.frames || 1;
  const scale = plan.scale || 4;
  const outMp = outMpFor({ cropW: plan.cropW || 1, cropH: plan.cropH || 1, scale });
  const est = bakeEstimate({ frames, model, mode, scale, outMp, hourly });

  // Checklist evaluation.
  const inventHit = INVENT_WORDS.filter((w) => (s.positive || '').toLowerCase().includes(w));
  const slowSet = model === 'supir' && mode === 'supir-only' && frames > 3;
  const cfgHot = Number(s.cfg) > 3;
  const churnHot = Number(s.churn) > 0;
  const notFaithful = Number(s.restore) <= 0;
  const stallMin = Math.round((poll.stallMs || 0) / 60000);
  const capMin = Math.round((poll.capMs || 0) / 60000);
  const capTooLow = est.perFrameSec > (poll.capMs || 0) / 1000;

  const warnings = [slowSet, cfgHot, churnHot, notFaithful, inventHit.length > 0, capTooLow].filter(Boolean).length;

  const proceed = async () => {
    setBusy(true);
    try {
      // Stamp an approval record onto config/upscale so the launch is gated on a
      // real, reviewed sign-off (operator/launcher checks approval before firing).
      await saveUpscaleConfig({
        approval: {
          at: Date.now(),
          plan: { label: plan.label, frames, scale, model, mode, lookName: plan.lookName || '' },
          settings: { cfg: s.cfg, churn: s.churn, restore: s.restore, steps: s.steps, refineDenoise: s.refineDenoise },
          estWallSec: Math.round(est.wallSec),
          estCostUsd: Math.round(est.costUsd * 100) / 100,
          warnings,
        },
      });
      setMsg('Approved — settings signed off and recorded.');
      if (onProceed) onProceed({ mode, model, scale, frames, est });
    } catch (e) { setMsg(`Approve failed: ${e.message}`); }
    setBusy(false);
  };

  return (
    <div className="cs-cfg-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="cs-cfg-modal">
        <div className="cs-cfg-head">
          <div className="cs-cfg-title">Bake preflight · {plan.label || 'upscale set'}</div>
          <button className="cs-btn cs-btn-sm cs-btn-ghost" onClick={onClose}>✕</button>
        </div>
        <div className="cs-cfg-sub">
          Review every effective setting before committing the GPU. All values are <b>live from Firestore</b> (<code>config/upscale</code>) — edit in ⚙ Upscale settings and they update here instantly.
        </div>
        <div className="cs-cfg-body">
          <div className="cs-cfg-group">
            <div className="cs-cfg-grouphd">The plan</div>
            <div className="cs-pf-list">
              <Row label="Frames" value={`${frames}`} note={plan.rangeNote} />
              <Row label="Scale" value={`${scale}×`} />
              <Row label="Output size" value={`≈ ${Math.round(outMp)} MP/frame`} />
              <Row label="Look" value={plan.lookName || '—'} />
              <Row ok={!!plan.subjectCentered} label="Subject centering" value={plan.subjectCentered ? 'on (tracked)' : 'off'} />
            </div>
          </div>

          <div className="cs-cfg-group">
            <div className="cs-cfg-grouphd">Upscaler settings (live)</div>
            <div className="cs-pf-list">
              <Row label="Model" value={model === 'supir' ? 'SUPIR' : model} />
              <Row warn={slowSet} label="Pipeline mode" value={mode}
                note={slowSet ? `supir-only ≈ ${fmtDuration(est.perFrameSec)}/frame — slow for ${frames} frames` : ''} />
              <Row warn={cfgHot} label="Prompt strength (cfg)" value={s.cfg}
                note={cfgHot ? 'high → can look AI/painterly; ≤2.5 stays honest' : 'honest'} />
              <Row warn={churnHot} label="Stochastic churn" value={s.churn}
                note={churnHot ? 'above 0 fabricates micro-detail' : 'no fabricated detail'} />
              <Row warn={notFaithful} label="Fidelity (restore)" value={s.restore}
                note={notFaithful ? '≤0 lets it invent freely; >0 = faithful' : 'faithful to source'} />
              <Row label="Steps" value={s.steps} />
              {mode !== 'supir-only' && <Row label="Two-stage finish denoise" value={s.refineDenoise} note="low = rides ESRGAN, less re-invention" />}
              <Row warn={inventHit.length > 0} label="Positive prompt" value={`"${(s.positive || '').slice(0, 48)}${(s.positive || '').length > 48 ? '…' : ''}"`}
                note={inventHit.length ? `contains invention words: ${inventHit.join(', ')}` : 'neutral/honest'} />
              <Row label="Negative prompt" value={`"${(s.negative || '').slice(0, 48)}…"`} />
            </div>
          </div>

          <div className="cs-cfg-group">
            <div className="cs-cfg-grouphd">Progress watchdog</div>
            <div className="cs-pf-list">
              <Row warn={capTooLow} label="Stall / cap timeout" value={`${stallMin}m no-progress · ${capMin}m cap`}
                note={capTooLow ? `cap < ${fmtDuration(est.perFrameSec)}/frame — raise the cap` : 'progress-aware (won\u2019t kill a live job)'} />
            </div>
          </div>

          <div className="cs-cfg-group">
            <div className="cs-cfg-grouphd">Estimate (measured on L40S)</div>
            <div className="cs-pf-list">
              <Row label="Per frame" value={`~${fmtDuration(est.perFrameSec)}`} />
              <Row warn={est.wallSec > 3 * 3600} label="Total wallclock" value={`~${fmtDuration(est.wallSec)}`}
                note={est.wallSec > 3 * 3600 ? 'long — consider two-stage or fewer frames' : 'serial on one GPU'} />
              <Row label="Est. GPU cost" value={`~${fmtUSD(est.costUsd)}`}
                note={<label style={{ display: 'inline-flex', gap: 4, alignItems: 'center' }}>@ $<input type="number" step="0.01" min="0" value={hourly} onChange={(e) => setHourly(Number(e.target.value) || 0)} style={{ width: 64 }} />/hr</label>} />
            </div>
          </div>
        </div>
        <div className="cs-cfg-foot">
          {msg && <span className="cs-cfg-msg">{msg}</span>}
          {warnings > 0 && <span className="cs-pf-warncount">⚠ {warnings} warning{warnings > 1 ? 's' : ''}</span>}
          <button className="cs-btn cs-btn-sm cs-btn-ghost" onClick={onClose} disabled={busy}>Cancel</button>
          <button className={`cs-btn cs-btn-sm ${warnings > 0 ? 'cs-btn-warn' : 'cs-btn-primary'}`} onClick={proceed} disabled={busy}>
            {busy ? 'Approving…' : warnings > 0 ? `Approve anyway (${warnings}⚠)` : '✓ Approve & proceed'}
          </button>
        </div>
      </div>
    </div>
  );
}
